/**
 * useSubscriptionData Hook
 * 
 * Responsabilidade: Carregar e gerenciar dados de assinatura do Stripe.
 * Foca exclusivamente em data fetching e estado de loading.
 * 
 * Princípios:
 * - Single Responsibility: Apenas carregamento de dados
 * - Separation of Concerns: Separado da lógica de UI
 */

import { useState, useEffect, useCallback } from 'react';
import { notify } from '../../../utils/toast';
import { useOrganization } from '../../../contexts/OrganizationContext';
import type {
    SubscriptionPlan as DomainPlan,
    OrganizationSubscription,
} from '../../../domain/entities/Subscription';
import {
    fetchAvailablePlans,
    fetchOrganizationSubscription,
    fetchOrganizationLimits
} from '../../../services/modules/subscriptionService';

// =============================================================================
// TIPOS
// =============================================================================

export interface UsageLimits {
    funnels: { current: number; max: number };
    contacts: { current: number; max: number };
    whatsapp: { current: number; max: number };
    users: { current: number; max: number };
}

export interface SubscriptionDataState {
    stripeSubscription: OrganizationSubscription | null;
    stripePlans: DomainPlan[];
    limits: UsageLimits | null;
    isLoading: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

export const useSubscriptionData = () => {
    const { currentOrganization } = useOrganization();

    // Estado
    const [stripeSubscription, setStripeSubscription] = useState<OrganizationSubscription | null>(null);
    const [stripePlans, setStripePlans] = useState<DomainPlan[]>([]);
    const [limits, setLimits] = useState<UsageLimits | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // =================================================================
    // CARREGAR DADOS DO STRIPE
    // =================================================================

    const loadStripeData = useCallback(async () => {
        if (!currentOrganization?.id) {
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);

            // Fetch data independently to prevent one error from blocking everything
            const [subscriptionResult, plansResult, limitsResult] = await Promise.allSettled([
                fetchOrganizationSubscription(currentOrganization.id),
                fetchAvailablePlans(),
                fetchOrganizationLimits(currentOrganization.id)
            ]);

            // Handle Subscription
            if (subscriptionResult.status === 'fulfilled') {
                setStripeSubscription(subscriptionResult.value);
            } else {
                console.error('[useSubscriptionData] Error fetching subscription:', subscriptionResult.reason);
                notify.error('Erro ao carregar status da assinatura');
            }

            // Handle Plans
            if (plansResult.status === 'fulfilled') {
                setStripePlans(plansResult.value);
            } else {
                console.error('[useSubscriptionData] Error fetching plans:', plansResult.reason);
                notify.error('Erro ao carregar planos disponíveis');
            }

            // Handle Limits (Optional - failure shouldn't block UI)
            if (limitsResult.status === 'fulfilled') {
                setLimits(limitsResult.value);
            } else {
                console.warn('[useSubscriptionData] Error fetching limits:', limitsResult.reason);
            }

        } catch (error) {
            console.error('[useSubscriptionData] Unexpected error:', error);
            notify.error('Houve um erro ao carregar os dados de assinatura');
        } finally {
            setIsLoading(false);
        }
    }, [currentOrganization?.id]);

    useEffect(() => {
        loadStripeData();
    }, [
        loadStripeData,
        currentOrganization?.plan,
        currentOrganization?.subscriptionStatus,
        currentOrganization?.status,
    ]);

    useEffect(() => {
        const onBillingUpdated = () => loadStripeData();
        window.addEventListener('crm:billing-updated', onBillingUpdated);
        return () => window.removeEventListener('crm:billing-updated', onBillingUpdated);
    }, [loadStripeData]);

    // =================================================================
    // RETORNO
    // =================================================================

    return {
        stripeSubscription,
        stripePlans,
        limits,
        isLoading,
        refreshData: loadStripeData,
    };
};
